import React from 'react';
import { Box, Stack, TextFieldProps, Typography } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';

import { InputAutocompleteMulti, InputSelect, Text, MenuItemType } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { FailureDefinitionFormValues } from '@Pages/FailureDefinition';
import { WEAVE_FONT } from '@Utils/fonts';

// Common shape for event/task which this component handles for now
export interface TypeWithFields {
	uploadId: string;
	name: string;
	fields: { key: string; type: string; display_name: string }[];
}

interface TypeWithDateFieldSelectorProps {
	typeInformation: TypeWithFields[];
	label: string;
	typeFieldName: keyof FailureDefinitionFormValues;
	dateFieldMapFieldName: 'eventDateMap' | 'taskDateMap';
	required?: boolean;
	textFieldProps?: TextFieldProps;
}

const Label = ({ children }: { children: string }) => (
	<Text variant="text-medium" sx={{ fontFamily: WEAVE_FONT, fontWeight: 400 }}>
		{children}
	</Text>
);

export const TypeWithDateFieldSelector: React.FC<TypeWithDateFieldSelectorProps> = ({
	typeInformation,
	label,
	typeFieldName,
	dateFieldMapFieldName,
	required,
	textFieldProps,
}) => {
	const { values, errors, touched, setFieldValue, submitCount } = useFormikContext<FailureDefinitionFormValues>();

	const { t } = useGlobalization();

	// Build select options for autocomplete
	const typeOptions = React.useMemo(
		() =>
			typeInformation?.map(type => ({
				key: type.uploadId,
				value: t(type.name),
			})) || [],
		[typeInformation, t],
	);

	const selectedTypes = Array.isArray(values[typeFieldName]) ? (values[typeFieldName] as string[]) : [];

	React.useEffect(() => {
		const selectedKeys = values[typeFieldName] || [];
		const mapKeys = Object.keys(values[dateFieldMapFieldName] || {});
		const toRemove = Array.isArray(selectedKeys) ? mapKeys.filter(key => !selectedKeys.includes(key)) : [];
		if (toRemove.length > 0) {
			toRemove.forEach(key => {
				setFieldValue(`${dateFieldMapFieldName}.${key}`, undefined);
			});
		}
	}, [values[typeFieldName], values[dateFieldMapFieldName], setFieldValue, dateFieldMapFieldName]);

	// Map uploadId to display name
	const typeKeyToLabel = React.useMemo(
		() => Object.fromEntries(typeOptions.map(({ key, value }) => [key, value])),
		[typeOptions],
	);

	// Map uploadId to available date fields
	const dateFieldsByTypeKey = React.useMemo(
		() =>
			typeInformation?.reduce((acc, group) => {
				acc[group.uploadId] = group.fields
					.filter(f => f.type === 'date')
					.map(f => ({
						key: f.key,
						value: f.key,
						label: f.display_name,
					}));
				return acc;
			}, {} as Record<string, MenuItemType[]>) || {},
		[typeInformation],
	);

	// Initialize error state for date fields
	const dateFieldErrorState: Record<string, { error: boolean; helperText: string }> = {};
	const dateFieldErrors = errors[dateFieldMapFieldName] as Record<string, string | undefined> | undefined;
	const dateFieldTouched = touched[dateFieldMapFieldName] as Record<string, boolean | undefined> | undefined;

	if (Array.isArray(selectedTypes)) {
		selectedTypes.forEach((typeKey: string) => {
			const hasError = !!dateFieldErrors?.[typeKey] && (!!dateFieldTouched?.[typeKey] || (submitCount ?? 0) > 0);

			const errorMsg = hasError ? dateFieldErrors?.[typeKey] : '';

			dateFieldErrorState[typeKey] = {
				error: hasError,
				helperText: errorMsg || '',
			};
		});
	}

	// Don't render if typeInformation is not available
	if (!typeInformation || typeInformation.length === 0) {
		return null;
	}

	return (
		<>
			<InputAutocompleteMulti
				fieldAttrs={{ name: typeFieldName }}
				options={typeOptions}
				label={label}
				textFieldProps={{
					required,
					fullWidth: true,
					style: { maxWidth: '550px' },
					...textFieldProps,
				}}
			/>
			{selectedTypes.length > 0 && (
				<Stack spacing={1}>
					<Typography
						component="h3"
						sx={{ fontFamily: WEAVE_FONT, fontSize: '0.9375rem', fontWeight: 700, color: '#111827' }}>
						Choose which date field indicates failure
					</Typography>
					<FieldArray name={dateFieldMapFieldName}>
						{() => (
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: '160px minmax(0, 360px)',
									columnGap: 2,
									rowGap: 1.5,
									alignItems: 'center',
								}}>
								{selectedTypes.map((typeKey: string) => (
									<React.Fragment key={typeKey}>
										<Label>{typeKeyToLabel[typeKey]}</Label>
										<InputSelect
											menuItems={dateFieldsByTypeKey[typeKey] || []}
											fieldAttrs={{
												name: `${dateFieldMapFieldName}.${typeKey}`,
											}}
											helperText={dateFieldErrorState[typeKey]?.helperText}
											selectProps={{
												label: 'Date Field',
												required: true,
												fullWidth: true,
												error: dateFieldErrorState[typeKey]?.error,
											}}
										/>
									</React.Fragment>
								))}
							</Box>
						)}
					</FieldArray>
				</Stack>
			)}
		</>
	);
};
